import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  BackgroundImage,
  Box,
  Button,
  Image,
  Card,
  Center,
  Grid,
  Group,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  useMantineColorScheme,
  useMantineTheme,
  Container,
  Modal,
  Divider,
} from "@mantine/core";
import {
  CalendarBlankIcon,
  CalendarIcon,
  LineSegmentsIcon,
  ListMagnifyingGlassIcon,
  MagnifyingGlass,
  PulseIcon,
  TimerIcon,
  HandHeartIcon,
  ShieldCheckIcon,
  TrendUpIcon,
} from "@phosphor-icons/react";
import { usePaths, useDashboard, useCheckHealthData, useNotifications, useMe } from "@/api";
import dayjs from "dayjs";

import NotificationPanel from "./notification-panel";

const HealthDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: pathsDataList } = usePaths({});
  const { data: dashboardData } = useDashboard();
  const { mutateAsync: getHealthData } = useCheckHealthData()
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [showAllPaths, setShowAllPaths] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [checkingHealthData, setCheckingHealthData] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [userFirstName, setUserFirstName] = useState("User");


  const { data: notificationsData } = useNotifications({
    page_size: 50,
  }, { query: { queryKey: ["notifications"] } });

  const formatNotificationDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const getActionWord = (title: string) => {
    if (title.toLowerCase().includes("analyzed")) {
      return "Analyzed";
    }
    return "Analyzed";
  };


  const { data: me } = useMe();
  useEffect(() => {
    setUserFirstName(me?.first_name || "User");
  }, [me?.first_name]);


  const getTimeAgo = (dateString: string) => {
    if (!dateString) return "Unknown";

    const now = dayjs();
    const updateDate = dayjs(dateString);
    const diffInDays = now.diff(updateDate, 'day');
    const diffInHours = now.diff(updateDate, 'hour');
    const diffInMinutes = now.diff(updateDate, 'minute');

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else if (diffInMinutes > 0) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else {
      return 'Just now';
    }
  };

  const handleStartPath = () => {
    // Open the loading modal
    setCheckingHealthData(true);

    // Fetch health data
    getHealthData()
      .then(() => {
        setCheckingHealthData(false);
        navigate("/app/path");
      })
      .catch((err) => {
        console.error(err);
        setCheckingHealthData(false);
      });
  };


  // Mobile Layout Component - Complete scrollable content
  const MobileLayout = () => (
    <Container size="sm" py="md" style={{ backgroundColor: "transparent" }}>
      <Stack gap="lg">
        {/* Welcome Hero */}
        <Card withBorder radius="lg" p={0} data-tour="health-background">
          <BackgroundImage
            src="/assets/home_background_image.jpg"
            radius="md"
            h={{ base: 180, sm: 200 }}
          >
            <Box
              p="md"
              h="100%"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <Stack gap="xs">
                <Text fw={600} size="lg" c="white">
                  Good morning, {userFirstName}!
                </Text>
                <Text size="sm" c="white" opacity={0.9}>
                  Let's get ready to continue your health journey today!
                </Text>
                <Button
                  mt="xs"
                  size="sm"
                  leftSection={<PulseIcon size={16} />}
                  onClick={handleStartPath}
                  style={{
                    background: "#124B8C",
                  }}
                >
                  Check My Health Data
                </Button>
              </Stack>
            </Box>
          </BackgroundImage>
        </Card>

        {/* Health Journey Summary - Mobile Optimized */}
        <Card withBorder radius="lg" p="md">
          <Stack gap="md">
            <Box>
              <Group gap={4} align="center">
                <TrendUpIcon size={25} color="#049B22" />
                <Text fw={600} size="md" mb="xs">
                  Your Health Journey Summary
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                See your progress, achievements, and milestones since Day 1.
              </Text>
            </Box>

            {/* Overall Progress Circle */}
            <Center>
              <Box ta="center">
                <svg width="0" height="0">
                  <defs>
                    <linearGradient
                      id="mobileRingGradient"
                      x1="1"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={theme.colors.red[4]} />
                      <stop offset="100%" stopColor="#003399" />
                    </linearGradient>
                  </defs>
                </svg>
                <RingProgress
                  size={120}
                  thickness={8}
                  sections={[
                    {
                      value: dashboardData?.overall_progress ?? 68,
                      color: "url(#mobileRingGradient)",
                    },
                  ]}
                  label={
                    <Stack gap={0} align="center">
                      <Text fw={700} size="lg">
                        {Math?.round(dashboardData?.overall_progress ?? 68)}%
                      </Text>
                      <Text size="xs" c="dimmed">
                        Complete
                      </Text>
                    </Stack>
                  }
                />
                <Text size="sm" fw={500} mt="xs">
                  Overall Progress
                </Text>
                <Text size="xs" c="dimmed">
                  Across all active OneSelf Path®
                </Text>
              </Box>
            </Center>

            {/* Stats Grid - Mobile Optimized */}
            <SimpleGrid cols={2} spacing="sm">
              <Box
                ta="center"
                bg={
                  colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : theme.colors.gray[0]
                }
                p="md"
                style={{ borderRadius: 12 }}
              >
                <Text fw={700} size="lg">
                  {dashboardData?.path_started ?? "5"}
                </Text>
                <Text size="xs" c="dimmed">
                  OneSelf Path® Started
                </Text>
              </Box>
              <Box
                ta="center"
                bg={
                  colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : theme.colors.gray[0]
                }
                p="md"
                style={{ borderRadius: 12 }}
              >
                <Text fw={700} size="lg">
                  {dashboardData?.path_completed ?? "3"}
                </Text>
                <Text size="xs" c="dimmed">
                  OneSelf Path® Completed
                </Text>
              </Box>
              <Box
                ta="center"
                bg={
                  colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : theme.colors.gray[0]
                }
                p="md"
                style={{ borderRadius: 12 }}
              >
                <Text fw={700} size="lg">
                  {dashboardData?.waypoints_completed ?? "7"}
                </Text>
                <Text size="xs" c="dimmed">
                  Waypoints
                </Text>
              </Box>
              <Box
                ta="center"
                bg={
                  colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : theme.colors.gray[0]
                }
                p="md"
                style={{ borderRadius: 12 }}
              >
                <Text fw={700} size="lg">
                  {dashboardData?.total_time_spent ?? "04"} hrs
                </Text>
                <Text size="xs" c="dimmed">
                  Time Engaged
                </Text>
              </Box>
            </SimpleGrid>

            {/* Date Info */}
            <Group justify="space-between">
              <Group gap="xs">
                <CalendarIcon size={16} color={theme.colors.gray[6]} />
                <Text size="xs" c="dimmed">
                  Joined{" "}
                  {dashboardData?.joined_date
                    ? dayjs(dashboardData?.joined_date).format("MMM D, YYYY")
                    : "July 1, 2024"}
                </Text>
              </Group>
              <Group gap="xs">
                <TimerIcon size={16} color={theme.colors.gray[6]} />
                <Text size="xs" c="dimmed">
                  Last update: {dashboardData?.last_update_date ? getTimeAgo(dashboardData.last_update_date) : ""}
                </Text>
              </Group>
            </Group>
          </Stack>
        </Card>

        {/* Feature Cards */}
        <SimpleGrid cols={3} spacing="xs">
          <Card withBorder radius="lg" p="sm" ta="center">
            <Stack gap="xs" align="center">
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: theme.colors.green[0],
                  border: `1px solid ${theme.colors.green[2]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HandHeartIcon size={16} color={theme.colors.green[6]} />
              </Box>
              <Text fw={600} size="xs">
                Personalized Insights
              </Text>
            </Stack>
          </Card>

          <Card withBorder radius="lg" p="sm" ta="center">
            <Stack gap="xs" align="center">
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: theme.colors.blue[0],
                  border: `1px solid ${theme.colors.blue[2]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PulseIcon size={16} color={theme.colors.blue[6]} />
              </Box>
              <Text fw={600} size="xs">
                Care Gap Analysis
              </Text>
            </Stack>
          </Card>

          <Card withBorder radius="lg" p="sm" ta="center">
            <Stack gap="xs" align="center">
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: theme.colors.orange[0],
                  border: `1px solid ${theme.colors.orange[2]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldCheckIcon size={16} color={theme.colors.orange[6]} />
              </Box>
              <Text fw={600} size="xs">
                Secure & Private
              </Text>
            </Stack>
          </Card>
        </SimpleGrid>

        {/* Quick Actions */}
        <Card withBorder radius="lg" p="md" bg={colorScheme === "dark" ? "#2E2E2E" : "white"}>
          <SimpleGrid cols={1} spacing="sm">
            <Card
              radius="md"
              p="sm"
              onMouseEnter={() => setHoveredCard("oneself-path-mobile")}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                background:
                  hoveredCard === "oneself-path-mobile"
                    ? colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : "linear-gradient(135deg, #FFF8F6 0%, #FAF8FB 40%, #F0F5FA 70%, #E8F2F8 90%)"
                    : colorScheme === "dark"
                      ? "#393939"
                      : theme.colors.informative[0],
                border: `1px solid ${hoveredCard === "oneself-path-mobile"
                  ? colorScheme === "dark" ? theme.colors.neutral[6] : theme.colors.neutral[4]
                  : "transparent"}`,
                transition: "background 0.2s ease, border 0.2s ease",
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group style={{ flex: 1, minWidth: 0 }}>
                  <ActionIcon
                    style={{
                      background: colorScheme === "dark"
                        ? "linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)"
                        : theme.colors.neutral[0],
                      transition: "background 0.2s ease",
                    }}
                  >
                    <LineSegmentsIcon size={18} color={colorScheme === "dark" ? "white" : theme.colors.indigo[8]} />
                  </ActionIcon>
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} size="sm" c={colorScheme === "dark" ? "white" : undefined}>
                      OneSelf Path®
                    </Text>
                    <Text size="xs" c={colorScheme === "dark" ? "white" : theme.colors.neutral[7]} style={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                      lineHeight: 1.3
                    }}>
                      Continue your personalized health Journey
                    </Text>
                  </Stack>
                </Group>
                <Text c={colorScheme === "dark" ? "#4B9BE5" : theme.colors.indigo[7]} size="sm" fw={500} style={{ cursor: "pointer" }} onClick={() => {
                  navigate("/app/path")
                }}>
                  Start Path
                </Text>
              </Group>
            </Card>

            <Card
              radius="md"
              p="sm"
              onMouseEnter={() => setHoveredCard("pathfinder-mobile")}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                background:
                  hoveredCard === "pathfinder-mobile"
                    ? colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : "linear-gradient(135deg, #FFF8F6 0%, #FAF8FB 40%, #F0F5FA 70%, #E8F2F8 90%)"
                    : colorScheme === "dark"
                      ? "#393939"
                      : theme.colors.informative[0],
                border: `1px solid ${hoveredCard === "pathfinder-mobile"
                  ? colorScheme === "dark" ? theme.colors.neutral[6] : theme.colors.neutral[4]
                  : "transparent"}`,
                transition: "background 0.2s ease, border 0.2s ease",
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group style={{ flex: 1, minWidth: 0 }}>
                  <ActionIcon
                    style={{
                      background: colorScheme === "dark"
                        ? "linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)"
                        : theme.colors.neutral[0],
                      transition: "background 0.2s ease",
                    }}
                  >
                    <ListMagnifyingGlassIcon size={18} color={colorScheme === "dark" ? "white" : theme.colors.indigo[8]} />
                  </ActionIcon>
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} size="sm" c={colorScheme === "dark" ? "white" : undefined}>
                      PathFinder
                    </Text>
                    <Text size="xs" c={colorScheme === "dark" ? "white" : theme.colors.neutral[7]} style={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                      lineHeight: 1.3
                    }}>
                      Ask question and get AI guidance
                    </Text>
                  </Stack>
                </Group>
                <Text c={colorScheme === "dark" ? "#4B9BE5" : theme.colors.indigo[7]} size="sm" fw={500} style={{ cursor: "pointer" }} onClick={() => {
                  navigate("/app/path-finder")
                }}>
                  Start Path
                </Text>
              </Group>
            </Card>
          </SimpleGrid>
        </Card>

        {/* Current Paths */}
        <Card withBorder radius="lg" p="md">
          <Group justify="space-between" mb="md">
            <Text fw={600} size="md">
              Your Current OneSelf Path®
            </Text>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setShowAllPaths(!showAllPaths)}
            >
              {showAllPaths ? "Less" : "More"}
            </Button>
          </Group>

          <Stack gap="sm">
            {pathsDataList?.results
              ?.slice(0, showAllPaths ? undefined : 2)
              ?.map((path) => ({
                status:
                  path.status === "completed"
                    ? "Completed"
                    : path?.status === "in_progress"
                      ? "In Progress"
                      : path.status === "new"
                        ? "Not started"
                        : path.status,
                name: path.name,
                progress: path.progress,
                date: path.start_date
                  ? new Date(path?.start_date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                  : "Not started",
              }))
              ?.map((path) => (
                <Card withBorder radius="lg" p="sm" key={path.name} bg={colorScheme === "dark" ? "#393939" : "white"}>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Group gap="xs">
                        <Image
                          src={
                            path?.status === "Completed"
                              ? "/assets/completed.png"
                              : path?.status === "In Progress"
                                ? "/assets/in_progress.png"
                                : "/assets/not-started-logo.png"
                          }
                          width={20}
                          height={20}
                          fit="contain"
                        />
                        <Text
                          size="xs"
                          fw={500}
                          c={
                            path?.status === "Completed"
                              ? colorScheme === "dark" ? "#4B9BE5" : theme.colors.indigo[5]
                              : path?.status === "In Progress"
                                ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[5]
                                : path?.status === "Not started"
                                  ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[5]
                                  : theme.colors.gray[6]
                          }
                        >
                          {path?.status}
                        </Text>
                      </Group>

                      {path.date !== "Not started" && (
                        <Text size="xs" c={colorScheme === "dark" ? theme.colors.neutral[2] : "dimmed"}>
                          {path.status === "Completed"
                            ? `Done ${path.date}`
                            : `Started ${path.date}`}
                        </Text>
                      )}
                    </Group>

                    <Text size="sm" fw={500} c={colorScheme === "dark" ? "white" : undefined}>
                      {path?.name}
                    </Text>

                    <Group align="center" gap="xs">
                      <Progress
                        value={path?.progress}
                        style={{ flex: 1 }}
                        size="xs"
                        color={
                          path?.progress === 100
                            ? theme.colors.indigo[6]
                            : undefined
                        }
                        styles={{
                          section: {
                            background:
                              path.progress === 100
                                ? theme.colors.indigo[6]
                                : `linear-gradient(90deg, ${theme.colors.red[3]} 0%, ${theme.colors.indigo[8]} 100%)`,
                          },
                        }}
                      />
                      <Text size="xs" fw={500} c={colorScheme === "dark" ? "white" : undefined}>
                        {path.progress}%
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              ))}
          </Stack>
        </Card>

        {/* Notifications */}
        <Card withBorder radius="lg" p="md">
          <Text fw={600} size="md" mb="md">
            Recent Notifications
          </Text>
          <Stack gap="sm">
            {dashboardData?.notifications?.slice(0, 2)?.map((item, index) => (
              <Card withBorder radius="lg" p="sm" key={index}>
                <Group gap="sm" align="flex-start">
                  <Box
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: theme.colors.red[5],
                      marginTop: 6,
                    }}
                  />
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Text fw={500} size="sm">
                      {item.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {item?.message}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatNotificationDate(item?.created_at)}
                    </Text>
                  </Stack>
                </Group>
              </Card>
            )) || (
                <>
                  <Card withBorder radius="lg" p="sm">
                    <Group gap="sm" align="flex-start">
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: theme.colors.red[5],
                          marginTop: 6,
                        }}
                      />
                      <Stack gap="xs" style={{ flex: 1 }}>
                        <Text fw={500} size="sm">
                          Health data Analyzed
                        </Text>
                        <Text size="xs" c="dimmed">
                          No changes detected in your care recommendations.
                        </Text>
                        <Text size="xs" c="dimmed">
                          Analyzed on July 15, 2025
                        </Text>
                      </Stack>
                    </Group>
                  </Card>

                  <Card withBorder radius="lg" p="sm">
                    <Group gap="sm" align="flex-start">
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: theme.colors.red[5],
                          marginTop: 6,
                        }}
                      />
                      <Stack gap="xs" style={{ flex: 1 }}>
                        <Text fw={500} size="sm">
                          Health data Retrieved
                        </Text>
                        <Text size="xs" c="dimmed">
                          New recommendations are available
                        </Text>
                        <Text size="xs" c="dimmed">
                          Retrieved on July 15, 2025
                        </Text>
                      </Stack>
                    </Group>
                  </Card>
                </>
              )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );

  // Desktop/Tablet Layout Component
  const DesktopLayout = () => (
    <Box p="sm" style={{ backgroundColor: "transparent" }}>
      <Card p={0} mb="md" radius="lg">
        <BackgroundImage
          src="/assets/home_background_image.jpg"
          radius="md"
          style={{
            backgroundPosition: "center",
            backgroundSize: "cover",
            height: 180,
            width: "100%",
          }}
        >
          <Box
            p="xl"
            style={{
              position: "absolute",
              top: 80,
              left: 0,
              right: 0,
              height: "100%",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0) 100%)",
              borderRadius: "inherit",
              zIndex: 1,
            }}
          >
            <Group justify="space-between" align="flex-start" h="100%">
              <Stack gap={4}>
                <Text fw={600} size="xl" c="white">
                  Good morning, {userFirstName}!
                </Text>
                <Text size="md" c="white" fw={600}>
                  Let's get ready to continue your health journey today!!!
                </Text>
              </Stack>

              <Button
                variant="filled"
                radius="md"
                mt={"sm"}
                leftSection={<PulseIcon size={16} />}
                onClick={handleStartPath}
                data-tour="check-health-data"
                style={{
                  background: "#124B8C",
                  border: "none",
                }}
              >
                Check My Health Data
              </Button>
            </Group>
          </Box>
        </BackgroundImage>
      </Card>

      <Stack gap="md">
        {/* Health Journey Summary */}
        <Box>
          <Grid>
            <Grid.Col span={9}>
              <Card withBorder radius="lg" h={180}>
                <Group justify="space-between" align="start" mb="md">
                  <Stack gap={"xs"}>
                    <Group gap={4} align="center">
                      <TrendUpIcon size={25} color="#049B22" />
                      <Text
                        fw={600}
                        c={
                          colorScheme === "dark"
                            ? theme.colors.neutral[0]
                            : theme.colors.neutral[9]
                        }
                      >
                        Your Health Journey Summary
                      </Text>
                    </Group>
                    <Text
                      size="sm"
                      c={
                        colorScheme === "dark"
                          ? theme.colors.neutral[3]
                          : theme.colors.neutral[7]
                      }
                    >
                      See your progress, achievements, and milestones since Day
                      1.
                    </Text>
                  </Stack>

                  <Stack gap={"sm"} align="end">
                    <Group gap={"xs"}>
                      <CalendarIcon
                        size={16}
                        color={
                          colorScheme === "dark"
                            ? theme.colors.neutral[3]
                            : theme.colors.neutral[7]
                        }
                      />
                      <Text
                        mr={8}
                        size="xs"
                        c={
                          colorScheme === "dark"
                            ? theme.colors.neutral[3]
                            : theme.colors.neutral[7]
                        }
                      >
                        Joined on{" "}
                        {dashboardData?.joined_date
                          ? dayjs(dashboardData?.joined_date).format("MMMM D, YYYY")
                          : ""}
                      </Text>
                    </Group>
                    <Group gap={"xs"}>
                      <TimerIcon
                        size={16}
                        color={
                          colorScheme === "dark"
                            ? theme.colors.neutral[3]
                            : theme.colors.neutral[7]
                        }
                      />
                      <Text
                        size="xs"
                        c={
                          colorScheme === "dark"
                            ? theme.colors.neutral[3]
                            : theme.colors.neutral[7]
                        }
                      >
                        Last update: {dashboardData?.last_update_date ? getTimeAgo(dashboardData.last_update_date) : ""}
                      </Text>
                    </Group>
                  </Stack>
                </Group>

                <Grid>
                  <Grid.Col span={3}>
                    <Box
                      ta="center"
                      bg={
                        colorScheme === "dark"
                          ? theme.colors.neutral[9]
                          : theme.colors.neutral[2]
                      }
                      p="sm"
                      style={{ borderRadius: 12 }}
                    >
                      <Text fw={700} size="lg">
                        {dashboardData?.path_started ?? ""}
                      </Text>
                      <Text size="sm">OneSelf Path® Started</Text>
                    </Box>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Box
                      ta="center"
                      bg={
                        colorScheme === "dark"
                          ? theme.colors.neutral[9]
                          : theme.colors.neutral[2]
                      }
                      p="sm"
                      style={{ borderRadius: 12 }}
                    >
                      <Text fw={700} size="lg">
                        {dashboardData?.path_completed ?? ""}
                      </Text>
                      <Text size="sm">OneSelf Path® Completed</Text>
                    </Box>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Box
                      ta="center"
                      bg={
                        colorScheme === "dark"
                          ? theme.colors.neutral[9]
                          : theme.colors.neutral[2]
                      }
                      p="sm"
                      style={{ borderRadius: 12 }}
                    >
                      <span data-tour="welcome" />
                      <Text fw={700} size="lg">
                        {dashboardData?.waypoints_completed ?? ""}
                      </Text>
                      <Text size="sm">Waypoints Completed</Text>
                    </Box>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Box
                      ta="center"
                      bg={
                        colorScheme === "dark"
                          ? theme.colors.neutral[9]
                          : theme.colors.neutral[2]
                      }
                      p="sm"
                      style={{ borderRadius: 12 }}
                    >
                      <Text fw={700} size="lg">
                        {dashboardData?.total_time_spent ?? ""} hrs
                      </Text>
                      <Text size="sm">Time Engaged</Text>
                    </Box>
                  </Grid.Col>
                </Grid>
              </Card>
            </Grid.Col>

            <Grid.Col span={3}>
              <Card
                withBorder
                radius="lg"
                ta="center"
                h={180}
                data-tour="health-journey"
              >
                <svg width="0" height="0">
                  <defs>
                    <linearGradient
                      id="overallRingGradient"
                      x1="1"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={theme.colors.red[4]} />
                      <stop offset="100%" stopColor="#003399" />
                    </linearGradient>
                  </defs>
                </svg>

                <Center>
                  <RingProgress
                    size={120}
                    thickness={5}
                    sections={[
                      {
                        value: dashboardData?.overall_progress ?? 0,
                        color: "url(#overallRingGradient)",
                      },
                    ]}
                    label={
                      <>
                        <Text fw={700} size="sm">
                          {Math?.round(dashboardData?.overall_progress ?? 0)}%
                        </Text>
                        <Text
                          size="sm"
                          c={
                            colorScheme === "dark"
                              ? theme.colors.neutral[3]
                              : theme.colors.neutral[7]
                          }
                        >
                          Complete
                        </Text>
                      </>
                    }
                  />
                </Center>
                <Text size="sm">Overall Progress</Text>
                <Text size="xs" c="dimmed">
                  Across all active OneSelf Path®
                </Text>
              </Card>
            </Grid.Col>
          </Grid>
        </Box>

        {/* Quick Actions */}
        <Card withBorder radius="lg" p="md" bg={colorScheme === "dark" ? "#2E2E2E" : "white"}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Card
              radius="md"
              p="sm"
              onMouseEnter={() => setHoveredCard("oneself-path-desktop")}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                background:
                  hoveredCard === "oneself-path-desktop"
                    ? colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : "linear-gradient(135deg, #FFF8F6 0%, #FAF8FB 40%, #F0F5FA 70%, #E8F2F8 100%)"
                    : colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : theme.colors.informative[0],
                border: `1px solid ${hoveredCard === "oneself-path-desktop"
                  ? colorScheme === "dark" ? theme.colors.neutral[6] : theme.colors.neutral[4]
                  : "transparent"}`,
                transition: "background 0.2s ease, border 0.2s ease",
              }}
            >
              <Group justify="space-between">
                <Group>
                  <ActionIcon
                    style={{
                      background: colorScheme === "dark"
                        ? "linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)"
                        : theme.colors.neutral[0],
                      transition: "background 0.2s ease",
                    }}
                  >
                    <LineSegmentsIcon size={20} color={colorScheme === "dark" ? "white" : theme.colors.indigo[8]} />
                  </ActionIcon>
                  <Stack gap={0}>
                    <Text fw={600} c={colorScheme === "dark" ? "white" : undefined}>OneSelf Path®</Text>
                    <Text
                      size="xs"
                      c={
                        colorScheme === "dark"
                          ? "white"
                          : theme.colors.neutral[7]
                      }
                    >
                      Continue your personalized health Journey
                    </Text>
                  </Stack>
                </Group>
                <Text c={colorScheme === "dark" ? "#4B9BE5" : theme.colors.indigo[7]} size="sm" fw={500} style={{ cursor: "pointer" }} onClick={() => {
                  navigate("/app/path")
                }}>
                  Start Path
                </Text>
              </Group>
            </Card>

            <Card
              radius="md"
              p="sm"
              onMouseEnter={() => setHoveredCard("pathfinder-desktop")}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                background:
                  hoveredCard === "pathfinder-desktop"
                    ? colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : "linear-gradient(135deg, #FFF8F6 0%, #FAF8FB 40%, #F0F5FA 70%, #E8F2F8 100%)"
                    : colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : theme.colors.informative[0],
                border: `1px solid ${hoveredCard === "pathfinder-desktop"
                  ? colorScheme === "dark" ? theme.colors.neutral[6] : theme.colors.neutral[4]
                  : "transparent"}`,
                transition: "background 0.2s ease, border 0.2s ease",
              }}
            >
              <Group justify="space-between">
                <Group>
                  <ActionIcon
                    style={{
                      background: colorScheme === "dark"
                        ? "linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)"
                        : theme.colors.neutral[0],
                      transition: "background 0.2s ease",
                    }}
                  >
                    <ListMagnifyingGlassIcon
                      size={20}
                      color={colorScheme === "dark" ? "white" : theme.colors.indigo[8]}
                    />
                  </ActionIcon>
                  <Stack gap={0}>
                    <Text fw={600} c={colorScheme === "dark" ? "white" : undefined}>PathFinder</Text>
                    <Text
                      size="xs"
                      c={
                        colorScheme === "dark"
                          ? "white"
                          : theme.colors.neutral[7]
                      }
                    >
                      Ask question and get AI guidance
                    </Text>
                  </Stack>
                </Group>
                <Text c={colorScheme === "dark" ? "#4B9BE5" : theme.colors.indigo[7]} size="sm" fw={500} style={{ cursor: "pointer" }} onClick={() => {
                  navigate("/app/path-finder")
                }}>
                  Start Path
                </Text>
              </Group>
            </Card>
          </SimpleGrid>
        </Card>

        <Grid>
          <Grid.Col span={8}>
            <Card withBorder radius="lg" data-tour="path-features" style={{ height: "100%" }}>
              <Group justify="space-between" >
                <Text
                  fw={600}
                  mb="md"
                  c={
                    colorScheme === "dark"
                      ? theme.colors.neutral[0]
                      : theme.colors.neutral[8]
                  }
                >
                  Your Current OneSelf Path®
                </Text>
                <Button
                  variant="transparent"
                  onClick={() => setShowAllPaths(!showAllPaths)}
                >
                  {showAllPaths ? "View Less" : "See All"}
                </Button>
              </Group>
              <Stack gap="md">
                {pathsDataList?.results
                  ?.slice(0, showAllPaths ? undefined : 2)
                  ?.map((path) => ({
                    status:
                      path.status === "completed"
                        ? "Completed"
                        : path?.status === "in_progress"
                          ? "In Progress"
                          : path.status === "new"
                            ? "Not started"
                            : path.status,
                    name: path.name,
                    progress: path.progress,
                    date: path.start_date
                      ? new Date(path?.start_date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                      : "Not started",
                  }))
                  ?.map((path) => (
                    <Card
                      withBorder
                      radius={"md"}
                      key={path.name}
                      p="md"
                      h={120}
                      bg={colorScheme === "dark" ? "#393939" : "white"}
                    >
                      <Group justify="space-between">
                        <Group gap={8} align="center" wrap="nowrap">
                          <Image
                            src={
                              path?.status === "Completed"
                                ? "/assets/completed.png"
                                : path?.status === "In Progress"
                                  ? "/assets/in_progress.png"
                                  : "/assets/not-started-logo.png"
                            }
                            width={25}
                            height={25}
                            fit="contain"
                            style={{ display: "block" }}
                          />
                          <Text
                            size="md"
                            fw={500}
                            style={{ whiteSpace: "nowrap" }}
                            c={
                              path?.status === "Completed"
                                ? colorScheme === "dark" ? "#4B9BE5" : theme.colors.indigo[5]
                                : path?.status === "In Progress"
                                  ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[5]
                                  : path?.status === "Not started"
                                    ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[5]
                                    : theme.colors.gray[6]
                            }
                          >
                            {path?.status}
                          </Text>
                        </Group>

                        {path.date !== "Not started" && (
                          <Group gap={"xs"}>
                            <CalendarBlankIcon
                              size={20}
                              color={
                                colorScheme === "dark"
                                  ? theme.colors.neutral[3]
                                  : theme.colors.neutral[7]
                              }
                            />
                            <Text size="sm" c={colorScheme === "dark" ? theme.colors.neutral[2] : "dimmed"}>
                              {path.status === "Completed"
                                ? `Completed on ${path.date}`
                                : `Started on ${path.date}`}
                            </Text>
                          </Group>
                        )}
                      </Group>
                      <Text size="md" fw={500} mt={"md"} c={colorScheme === "dark" ? "white" : undefined}>
                        {path?.name}
                      </Text>

                      <Group align="center" gap="xs">
                        <Progress
                          value={path?.progress}
                          style={{ flex: 1 }}
                          size="sm"
                          color={
                            path?.progress === 100
                              ? theme.colors.indigo[6]
                              : undefined
                          }
                          styles={{
                            root: { position: "relative" },
                            section: {
                              background:
                                path.progress === 100
                                  ? theme.colors.indigo[6]
                                  : `linear-gradient(90deg, ${theme.colors.red[3]} 0%, ${theme.colors.indigo[8]} 100%)`,
                            },
                          }}
                        />
                        <Text size="xs" fw={500}>
                          {path.progress}%
                        </Text>
                      </Group>
                    </Card>
                  ))}
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={4}>
            <Card withBorder radius="lg" style={{ height: "100%" }}>
              <Group justify="space-between" mb="md">
                <Text
                  fw={600}
                  c={
                    colorScheme === "dark"
                      ? theme.colors.neutral[0]
                      : theme.colors.neutral[8]
                  }
                >
                  Notifications
                </Text>
                <Button
                  variant="transparent"
                  onClick={() => setNotificationPanelOpen(true)}
                >
                  See All
                </Button>
              </Group>

              <Stack gap="md">
                {notificationsData?.results?.slice(0, 2).map((notification) => (
                  <Box key={notification.id}>
                    <Group gap="sm" align="flex-start" wrap="nowrap">
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.colors.red[5],
                          marginTop: 6,
                          flexShrink: 0,
                        }}
                      />
                      <Stack gap={4} style={{ flex: 1 }}>
                        <Group justify="space-between" align="center" wrap="nowrap">
                          <Text
                            fw={600}
                            size="sm"
                            c={theme.colors.blue[6]}
                            style={{ flex: 1 }}
                          >
                            {notification.title}
                          </Text>
                          <Group gap={4} wrap="nowrap">
                            <CalendarIcon
                              size={14}
                              color={colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6]}
                            />
                            <Text
                              size="xs"
                              c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[7]}
                              style={{ whiteSpace: "nowrap" }}
                            >
                              {getActionWord(notification.title)} on {formatNotificationDate(notification.created_at)}
                            </Text>
                          </Group>
                        </Group>
                        <Text
                          size="xs"
                          c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[7]}
                        >
                          {notification.message}
                        </Text>
                      </Stack>
                    </Group>
                  </Box>
                )) || (
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      No notifications yet
                    </Text>
                  )}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>

      {notificationPanelOpen && (
        <NotificationPanel
          isOpen={notificationPanelOpen}
          onClose={() => setNotificationPanelOpen(false)}
        />
      )}
    </Box>
  );

  return (
    <Box style={{ backgroundColor: "transparent" }}>
      {/* Mobile Layout - shown on mobile only */}
      <Box hiddenFrom="md" style={{ backgroundColor: "transparent" }}>
        <MobileLayout />
      </Box>

      {/* Desktop Layout - shown on desktop/tablet */}
      <Box visibleFrom="md" style={{ backgroundColor: "transparent" }}>
        <DesktopLayout />
      </Box>

      {/* Checking Health Data Modal */}
      <Modal
        opened={checkingHealthData}
        onClose={() => setCheckingHealthData(false)}
        title="Checking Health Data"
        size="md"
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={true}
        styles={{
          body: {
            paddingTop: 0,
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Divider mb="xl" />
        <Stack align="center" gap="xl" pb="xl" px="xl">
          {/* Main heading */}
          <Text
            size="xl"
            fw={600}
            c="#124B8C"
            ta="center"
          >
            Checking Your Health Data...
          </Text>

          {/* Animated Loader Circle */}
          <Box
            style={{
              width: 80,
              height: 80,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Solid base circle */}
            <Box
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                border: "3px solid #E5E7EB",
              }}
            />

            {/* Rotating blue border segment */}
            <Box
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                border: "3px solid transparent",
                borderTopColor: "#124B8C",
                borderRightColor: "#124B8C",
                animation: "spin 2s linear infinite",
              }}
            />

            {/* Static icon in center */}
            <MagnifyingGlass size={32} color="#FF6B6B" weight="regular" />

            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </Box>

          {/* Status Text */}
          <Text
            size="md"
            c={colorScheme === "dark" ? "gray.4" : "dark.5"}
            fw={500}
            ta="center"
          >
            Fetching your health records
          </Text>
        </Stack>
      </Modal>
    </Box>
  );
};

export default HealthDashboard;
